import { type CSSProperties, type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../api/client'
import { Divider, GoogleButton } from '../components/GoogleButton'
import { detectWebView } from '../auth/detectWebView'
import { languageFromPath } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// Passwordless, email-first login inside a Nimvoy-styled card. Two states:
// the entry screen (Google + email → "Continue with email") and the "Check
// your email" screen after a link is sent. No password field — accounts are
// Google or passwordless (magic-link); the backend creates one on the fly for
// first-time emails, so login and signup are the same flow. Password login is
// retired from the UI (the backend capability stays dormant).

const cardStyle: CSSProperties = {
  background: '#ffffff',
  border: '1px solid #E7E1D4',
  borderRadius: 16,
  boxShadow: '0 6px 28px rgba(11, 31, 58, 0.06)',
  padding: '36px 32px',
}

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="page-stub" style={{ maxWidth: 440 }}>
      <div style={cardStyle}>{children}</div>
    </div>
  )
}

export default function Login() {
  const location = useLocation()
  const { t } = useTranslation('auth')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.titleLogin'), noindex: true })
  const [params, setParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [banner, setBanner] = useState<string | null>(null)

  const webview = useMemo(() => detectWebView(), [])

  useEffect(() => {
    const magic = params.get('magic')
    if (magic && ['expired', 'used', 'invalid'].includes(magic)) {
      setBanner(t(`login.magicBanners.${magic}`))
      params.delete('magic')
      setParams(params, { replace: true })
    } else if (params.get('error') === 'google_email_unverified') {
      // The Google callback refuses an address Google hasn't verified — it
      // proves nothing about the inbox. The email link below still works.
      setBanner(t('login.googleEmailUnverified'))
      params.delete('error')
      setParams(params, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function sendLink() {
    setError(null)
    setSubmitting(true)
    try {
      await apiFetch<void>('/api/auth/magic/request', {
        method: 'POST',
        body: JSON.stringify({ email, locale: lang }),
      })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.errorMagicSendFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void sendLink()
  }

  // --- "Check your email" screen ---
  if (sent) {
    return (
      <AuthCard>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: '#F1EEE6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0B1F3A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
              <path d="M3.5 6.5l8.5 6 8.5-6" />
            </svg>
          </div>
        </div>

        <h1 className="h1" style={{ marginBottom: 14, fontSize: 26, textAlign: 'center' }}>{t('login.checkTitle')}</h1>

        <p className="muted" style={{ fontSize: 15, lineHeight: 1.5, margin: '0 0 6px', textAlign: 'center' }}>
          {t('login.checkLead')}
        </p>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', margin: '0 0 22px', textAlign: 'center', wordBreak: 'break-word' }}>
          {email}
        </p>

        <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 24px', textAlign: 'center' }}>
          {t('login.checkExpiry')}
        </p>

        {error && <div style={{ color: 'var(--pop)', fontSize: 14, marginBottom: 14, textAlign: 'center' }}>{error}</div>}

        <button
          type="button"
          onClick={() => void sendLink()}
          disabled={submitting}
          style={{
            display: 'block',
            width: '100%',
            background: '#ffffff',
            color: 'var(--ink)',
            border: '1.5px solid #D7D2C6',
            borderRadius: 10,
            padding: '11px 18px',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            marginBottom: 16,
          }}
        >
          {submitting ? t('login.submittingMagic') : t('login.checkResend')}
        </button>

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => { setSent(false); setError(null) }}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ink-3)', cursor: 'pointer', fontSize: 14 }}
          >
            {t('login.checkDifferent')}
          </button>
        </div>
      </AuthCard>
    )
  }

  // --- Entry screen ---
  return (
    <AuthCard>
      <h1 className="h1" style={{ marginBottom: 24, fontSize: 28, textAlign: 'center' }}>{t('login.title')}</h1>

      {banner && (
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
            borderRadius: 10,
            fontSize: 14,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          {banner}
        </div>
      )}

      {webview ? (
        <div className="webview-warn" style={{ marginBottom: 16 }}>
          <div className="webview-warn__title">
            {t('login.webviewTitle', { name: webview.name })}
          </div>
        </div>
      ) : (
        <>
          <GoogleButton label={t('login.googleLabel')} />
          <Divider />
        </>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label className="label">{t('login.labelEmail')}</label>
          <input
            className="input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder={t('login.magicEmailPlaceholder')}
          />
        </div>
        {error && <div style={{ color: 'var(--pop)', fontSize: 14 }}>{error}</div>}
        <button type="submit" className="btn primary" disabled={submitting}>
          {submitting ? t('login.submittingMagic') : t('login.continueEmail')}
        </button>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'center' }}>
          {t('login.emailHint')}
        </p>
      </form>
    </AuthCard>
  )
}
